import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ApiErrorCodes,
  parseApiResponseError,
} from '../../../../api/api-error';
import apiManager from '../../../../api/api-manager';
import {
  AggiornaCellulareError,
  AggiornaCellulareErrorCodiciErroreEnum,
  TimeoutOTP,
} from '../../../../api/generated/apc/api';
import { getAPIRequestHeaders } from '../../../../utils/api-utils';
import { AsyncThunkConfig } from '../../../async-thunk-config';
import {
  ChangePhoneNumberState,
  InsertPhoneNumberChangePhoneNumberError,
  VerificationPhoneNumberChangePhoneNumberError,
} from './change-phone-number-type';
import {
  verifyInsertPhoneNumberChangePhoneNumberFields,
  verifyInsertPhoneNumberChangeSecondaryPhoneNumberFields,
} from './change-phone-number-utils';
import { getVerificationEmailOrPhoneNumberError } from '../../../../utils/error-utils';
import { parsePhoneNumberForServer } from '../../../../api/parsers/commons-api-parsers';
import { UserState } from '../../../data';

export const insertPhoneNumberChangePhoneNumber = createAsyncThunk<
  TimeoutOTP,
  void,
  AsyncThunkConfig<InsertPhoneNumberChangePhoneNumberError>
>('insert_phone_number_change_phone_number', async (_, thunkApi) => {
  try {
    const userState = thunkApi.getState().data.user;
    const state = thunkApi.getState().features.changePhoneNumber;

    const validationError =
      verifyInsertPhoneNumberChangePhoneNumberFields(state);
    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    const response = await apiManager.changeContactApi.cambioCellulare(
      userState.username,
      {
        cellulare: parsePhoneNumberForServer(state.phoneNumber),
      },
      getAPIRequestHeaders(thunkApi)
    );
    return response.data;
  } catch (error) {
    const apiError = parseApiResponseError<AggiornaCellulareError>(error);
    const errorCodes = apiError.payload?.codiciErrore;

    if (apiError.responseErrorCode === ApiErrorCodes.BAD_REQUEST) {
      if (errorCodes?.length > 0) {
        switch (errorCodes[0]) {
          case AggiornaCellulareErrorCodiciErroreEnum.CellulareNonUtilizzabile:
            return thunkApi.rejectWithValue('CELLULARE_NON_UTILIZZABILE');
        }
      }
    }
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const verificationPhoneNumberChangePhoneNumber = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig<VerificationPhoneNumberChangePhoneNumberError>
>('verification_phone_number_change_phone_number', async (_, thunkApi) => {
  try {
    const userState = thunkApi.getState().data.user;
    const state: ChangePhoneNumberState =
      thunkApi.getState().features.changePhoneNumber;

    await apiManager.changeContactApi.verificaCambioCellulare(
      userState.username,
      {
        cellulare: parsePhoneNumberForServer(state.phoneNumber),
        codiceDiVerificaEmail: state.changePhoneNumberVerificationCodeEmail,
        codiceDiVerificaCellulare:
          state.changePhoneNumberVerificationCodePhoneNumber,
      },
      getAPIRequestHeaders(thunkApi)
    );
    return null;
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue(
      getVerificationEmailOrPhoneNumberError(error)
    );
  }
});

export const insertPhoneNumberChangeSecondaryPhoneNumber = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig<InsertPhoneNumberChangePhoneNumberError>
>('insert_phone_number_change_secondary_phone_number', async (_, thunkApi) => {
  try {
    const userState: UserState = thunkApi.getState().data.user;
    const state: ChangePhoneNumberState =
      thunkApi.getState().features.changePhoneNumber;

    const validationError =
      verifyInsertPhoneNumberChangeSecondaryPhoneNumberFields(
        state,
        userState.phone
      );
    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    const response =
      await apiManager.changeContactApi.cambioCellulareSecondario(
        userState.username,
        {
          cellulare: parsePhoneNumberForServer(state.secondaryPhoneNumber),
        },
        getAPIRequestHeaders(thunkApi)
      );
    return response.data;
  } catch (error) {
    const apiError = parseApiResponseError<AggiornaCellulareError>(error);
    const errorCodes = apiError.payload?.codiciErrore;

    if (apiError.responseErrorCode === ApiErrorCodes.BAD_REQUEST) {
      if (errorCodes?.length > 0) {
        switch (errorCodes[0]) {
          case AggiornaCellulareErrorCodiciErroreEnum.CellulareNonUtilizzabile:
            return thunkApi.rejectWithValue('CELLULARE_NON_UTILIZZABILE');
        }
      }
    }
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});
